import { CategoryKey, CategoryTypeKey } from "../deduction";

export interface ILastMessage {
  messageId: number;
  messagePreview: string;
  attachmentCount: number;
  sender: IConversationUser;
}

export interface IConversation {
  id: number;
  referenceId: number;
  remittanceItemId?: number;
  categoryKey: CategoryKey;
  categoryTypeKey?: CategoryTypeKey;
  invoiceNumber: string;
  checkNumber: string;
  purchaserKey?: string;
  purchaserName?: string;
  participants: IConversationParticipant[];
  createdAt: string;
  updatedAt?: string | null;
  lastMessage: ILastMessage;
  unreadMessageCount: number;
}
export interface IConversationParticipant extends IConversationUser {
  participantId?: number;
}

export interface IConversationUser {
  email?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  avatar?: string | null;
  isDeleted?: boolean;
}

export class Conversation implements IConversation {
  private readonly _id: number;
  private readonly _referenceId: number;
  private readonly _remittanceItemId?: number;
  private readonly _categoryKey: CategoryKey;
  private readonly _categoryTypeKey?: CategoryTypeKey;
  private readonly _invoiceNumber: string;
  private readonly _checkNumber: string;
  private readonly _purchaserKey?: string;
  private readonly _purchaserName?: string;
  private readonly _participants: IConversationParticipant[];
  private readonly _createdAt: string;
  private readonly _updatedAt?: string | null;
  private readonly _lastMessage?: ILastMessage;
  private _unreadMessageCount: number;

  constructor(data: IConversation) {
    this._id = data.id;
    this._referenceId = data.referenceId;
    this._remittanceItemId = data.remittanceItemId;
    this._categoryKey = data.categoryKey;
    this._categoryTypeKey = data.categoryTypeKey;
    this._invoiceNumber = data.invoiceNumber;
    this._checkNumber = data.checkNumber;
    this._purchaserKey = data.purchaserKey;
    this._purchaserName = data.purchaserName;
    this._participants = data.participants?.map(participant => new ConversationParticipant(participant)) ?? [];
    this._createdAt = data.createdAt;
    this._updatedAt = data.updatedAt;
    this._lastMessage = data.lastMessage ? new LastMessage(data.lastMessage) : undefined;
    this._unreadMessageCount = data.unreadMessageCount ?? 0;
  }

  get id(): number {
    return this._id;
  }

  get referenceId(): number {
    return this._referenceId;
  }

  get remittanceItemId(): number | undefined {
    return this._remittanceItemId;
  }

  get categoryKey(): CategoryKey {
    return this._categoryKey;
  }

  get categoryTypeKey(): CategoryTypeKey | undefined {
    return this._categoryTypeKey;
  }

  get invoiceNumber(): string {
    return this._invoiceNumber;
  }

  get checkNumber(): string {
    return this._checkNumber;
  }

  get purchaserKey(): string | undefined {
    return this._purchaserKey;
  }

  get purchaserName(): string | undefined {
    return this._purchaserName;
  }

  get participants(): IConversationParticipant[] {
    return this._participants;
  }

  get createdAt(): string {
    return this._createdAt;
  }

  get updatedAt(): string | null {
    return this._updatedAt ?? null;
  }

  get lastMessage(): ILastMessage {
    return this._lastMessage!;
  }

  get unreadMessageCount(): number {
    return this._unreadMessageCount;
  }

  public incrementUnreadMessageCount(): void {
    this._unreadMessageCount++;
  }

  public resetUnreadMessageCount(): void {
    this._unreadMessageCount = 0;
  }
}

export class LastMessage implements ILastMessage {
  private readonly _messageId: number;
  private readonly _messagePreview: string;
  private readonly _attachmentCount: number;
  private readonly _sender: IConversationUser;

  constructor(data: ILastMessage) {
    this._messageId = data.messageId;
    this._messagePreview = data.messagePreview;
    this._attachmentCount = data.attachmentCount;
    this._sender = new ConversationUser(data.sender);
  }

  get messageId(): number {
    return this._messageId;
  }

  get messagePreview(): string {
    return this._messagePreview;
  }

  get attachmentCount(): number {
    return this._attachmentCount;
  }

  get sender(): IConversationUser {
    return this._sender;
  }
}

export class ConversationParticipant implements IConversationParticipant {
  private readonly _participantId?: number;
  private readonly _email?: string;
  private readonly _firstName?: string;
  private readonly _lastName?: string;
  private readonly _fullName?: string;
  private readonly _avatar?: string | null;
  private readonly _isDeleted?: boolean;

  constructor(data: IConversationParticipant) {
    this._participantId = data?.participantId;
    this._email = data.email;
    this._firstName = data.firstName;
    this._lastName = data.lastName;
    this._fullName = data.fullName;
    this._avatar = data.avatar;
    this._isDeleted = data.isDeleted;
  }

  get participantId(): number | undefined {
    return this._participantId;
  }

  get email(): string | undefined {
    return this._email;
  }

  get firstName(): string | undefined {
    return this._firstName;
  }

  get lastName(): string | undefined {
    return this._lastName;
  }

  get fullName(): string | undefined {
    return this._fullName;
  }

  get avatar(): string | null | undefined {
    return this._avatar;
  }

  get isDeleted(): boolean | undefined {
    return this._isDeleted;
  }
}

export class ConversationUser implements IConversationUser {
  private readonly _email?: string;
  private readonly _firstName?: string;
  private readonly _lastName?: string;
  private readonly _fullName?: string;
  private readonly _avatar?: string | null;
  private readonly _isDeleted?: boolean;

  constructor(data: IConversationUser) {
    this._email = data?.email;
    this._firstName = data?.firstName;
    this._lastName = data?.lastName;
    this._fullName = data?.fullName;
    this._avatar = data?.avatar;
    this._isDeleted = data?.isDeleted;
  }

  get email(): string | undefined {
    return this._email;
  }

  get fullName(): string | undefined {
    return this._fullName;
  }

  get firstName(): string | undefined {
    return this._firstName;
  }

  get lastName(): string | undefined {
    return this._lastName;
  }

  get avatar(): string | null | undefined {
    return this._avatar;
  }

  get isDeleted(): boolean | undefined {
    return this._isDeleted;
  }
}
